// Static configuration for the cat AI: types, behavior tables, pose
// graph, and tuning constants. Pulled out of cat.ts to keep that file
// focused on the loadCat orchestrator + per-frame update loop.

export type CatPose = "stand" | "sit" | "lie" | "sleep";

export interface Behavior {
  clip: string;
  pose: CatPose;
  loop: boolean;
  /** World units per second of forward motion along the cat's heading.
   * Only meaningful when pose === "stand". */
  speed: number;
  durMin?: number;
  durMax?: number;
  weight: number;
}

export interface CatKeyInput {
  forward: boolean;
  backward: boolean;
  left: boolean;
  right: boolean;
  /** Shift modifier — upgrades forward locomotion from Walk_F to Run_F.
   * Has no effect without `forward` (or a waypoint). */
  run: boolean;
}

// 2D roam zone — a rectangle on the floor in front of the shelf. The
// shelf is its own dynamic body now and reacts to contact, so the cat
// is allowed to walk right up to it (and past it, on the other side).
export const X_MIN = -22.5;
export const X_MAX = 22.5;
export const Z_MIN = 0.5;
export const Z_MAX = 15.0;
export const FLOOR_Y = 0;
export const SCALE = 7.5;
export const FADE = 0.25;

// Heading convention: with `model.rotation.y = h`, the cat's local
// forward (+X in the FBX) maps to world (sin(h), 0, cos(h)). h=0 →
// faces +Z (toward shelf); h=π/2 → faces +X (right); h=-π/2 → faces
// -X (left); h=π → faces -Z (away from shelf).
export const INITIAL_HEADING = Math.PI / 2;

// Heading-delta thresholds for picking a Turn clip. Below SMALL_TURN
// we just snap; SMALL→MEDIUM uses Turn-45_L/R; above MEDIUM uses
// Turn-90_L/R.
export const SMALL_TURN = Math.PI / 9; // 20°
export const MEDIUM_TURN = Math.PI / 2.5; // 72°

// Threshold for firing a Turn clip during waypoint pursuit. Higher
// than SMALL_TURN because the Turn-45 clip swings a full 45° and
// overshoots: at |dh| just over SMALL_TURN, post-clip |dh| > SMALL_TURN
// in the opposite direction, which the rising-edge detector reads as a
// new turn intent and fires Turn-45 the OTHER way — leaving the cat
// thrashing L-R-L-R until the user releases the waypoint. Setting
// threshold ≥ 45° − SMALL_TURN guarantees post-clip |dh| ≤ SMALL_TURN
// after a Turn-45 sign-flip, killing the oscillation. We use π/6 (30°)
// for a small extra margin; smooth steering at KEY_TURN_RATE handles
// any 0-30° residual in <0.2s.
export const WAYPOINT_TURN_THRESHOLD = Math.PI / 6; // 30°

// Per-pose multiplier applied to candidate weights when the candidate
// shares the cat's current pose. Stand has many behaviors (walk / look /
// eat / drink / …) so a mild bias keeps it varied; sit/lie/sleep have
// far fewer pool entries, and without a strong bias the AI yanks the
// cat back to stand on most re-picks — which the user perceives as the
// cat "sitting twice" (sit, stand, sit, stand). Tuned so the cat
// settles in a non-stand pose for ~3-5 behavior cycles before the dice
// pull it back out.
export const POSE_STAY_BIAS: Record<CatPose, number> = {
  stand: 1.5,
  sit: 10,
  lie: 12,
  sleep: 20,
};

// Continuous turn rate (rad/s) for both WASD A/D and waypoint steering.
// Tuned to feel responsive without snapping — π/s = 180°/s.
export const KEY_TURN_RATE = Math.PI;
// Distance (world units) at which the cat stops chasing a waypoint.
// Roughly half the cat's own length — close enough to feel "arrived."
export const WAYPOINT_ARRIVE_RADIUS = 1.2;
// Switch from Walk_F to Trot_F when the waypoint is at least this far
// away. Keeps short-range hops walking, makes long-haul fetches feel
// energetic.
export const WAYPOINT_TROT_DISTANCE = 8;

// The looped idle clip per pose. After any pose transition, if no
// follow-up behavior is queued, the cat settles into this clip.
export const POSE_IDLE: Record<CatPose, string> = {
  stand: "Base",
  sit: "Sit_Idle",
  lie: "Lie_idle",
  sleep: "Sleep_idle",
};

// Direct pose transition clips (one entry per edge). Non-adjacent
// transitions are planned via BFS in `planTransitions` — sit↔lie etc.
// route through stand, sit↔sleep routes through stand+lie.
//
// Naming convention from the pack (verified empirically by sampling
// Sit_from / Sit_to / Lie_from / Lie_to spine height across the clip's
// duration — see scripts/tests/test-cat-clip-boundaries.mjs trajectory
// output): the named pose is the *fixed end* of the clip.
//   `Sit_to`   = transition INTO sit (stand → sit, clamps in sit).
//                Spine height descends start→end.
//   `Sit_from` = transition OUT OF sit (sit → stand). Spine height
//                rises start→end.
// A previous version of this comment had the convention inverted —
// the AI would pick `Sit_from` for stand→sit, which played a sit→stand
// rise (cat snapped to sit at frame 0, rose to stand, then snapped
// back to sit when Sit_Idle started). User-visible symptom was "cat
// sits, resets to stand, sits again."
//
// `Sleep_to` / `Sleep_from` are special: their spine stays flat at
// lie-pose height throughout, so they're lie↔sleep edges, not stand↔
// sleep. Stand → sleep therefore routes Lie_to + Sleep_to (and back
// Sleep_from + Lie_from). Wiring sleep directly off stand snaps the
// cat from upright to flat at frame 0 of Sleep_to.
export const POSE_TRANSITIONS: Partial<
  Record<`${CatPose}->${CatPose}`, string>
> = {
  "stand->sit": "Sit_to",
  "sit->stand": "Sit_from",
  "stand->lie": "Lie_to",
  "lie->stand": "Lie_from",
  "lie->sleep": "Sleep_to",
  "sleep->lie": "Sleep_from",
};

export const BEHAVIORS: Behavior[] = [
  // ─── stand ─────────────────────────────────────────────────────────
  // Locomotion. Speeds calibrated from each clip's authored Root
  // translation (logged by scripts/build/fbx-to-glb.py before the strip):
  // model_u/s × SCALE=7.5. Bumping SCALE without rescaling these will
  // reintroduce foot-sliding.
  {
    clip: "Walk_F",
    pose: "stand",
    loop: true,
    speed: 1.734,
    durMin: 3,
    durMax: 8,
    weight: 5,
  },
  {
    clip: "Trot_F",
    pose: "stand",
    loop: true,
    speed: 4.641,
    durMin: 2,
    durMax: 5,
    weight: 2,
  },
  {
    clip: "Run_F",
    pose: "stand",
    loop: true,
    speed: 11.634,
    durMin: 1,
    durMax: 3,
    weight: 1,
  },
  // Idle loop
  {
    clip: "Base",
    pose: "stand",
    loop: true,
    speed: 0,
    durMin: 1.5,
    durMax: 4,
    weight: 4,
  },
  // Head turns + yawn. LookDown and Stretching dropped (regression
  // test caught them: head dives -1.24 / -0.95 below spine — "rear
  // lifted" symptom).
  { clip: "Look", pose: "stand", loop: false, speed: 0, weight: 2 },
  { clip: "LookSite", pose: "stand", loop: false, speed: 0, weight: 1 },
  { clip: "Yaw", pose: "stand", loop: false, speed: 0, weight: 1 },
  // Action one-shots. Drink_U/Eat_U/SharpensClaws are excluded from
  // KEEP_CLIPS upstream — they rear the cat up against a non-existent
  // prop (high bowl, scratching post) and read as "cat floating with
  // rear lifted, clawing air."
  { clip: "Drink_D", pose: "stand", loop: false, speed: 0, weight: 1 },
  { clip: "Eat_D", pose: "stand", loop: false, speed: 0, weight: 1 },
  { clip: "Pet", pose: "stand", loop: false, speed: 0, weight: 1 },
  { clip: "Digging", pose: "stand", loop: false, speed: 0, weight: 1 },
  { clip: "Shaking", pose: "stand", loop: false, speed: 0, weight: 1 },
  // In-place hop. fbx-to-glb.py preserves Root.y (the jump arc) and
  // strips Root.x/z so the cat doesn't drift forward through the
  // shelf. Speed=0 — the AI doesn't translate during the clip; the
  // visible motion is all in the authored Y curve + leg cycle.
  { clip: "InPlace", pose: "stand", loop: false, speed: 0, weight: 1 },
  // Combat swipes (Bite/Paw) removed — they crouch into a head-below-
  // spine pounce-prep pose. See test-cat-clips.mjs for the regression
  // that caught them.

  // ─── sit ───────────────────────────────────────────────────────────
  // Licking_sit dropped — buries the head down to the paw and reads
  // as a broken sit (test: head -0.37 below spine).
  {
    clip: "Sit_Idle",
    pose: "sit",
    loop: true,
    speed: 0,
    durMin: 6,
    durMax: 12,
    weight: 4,
  },
  { clip: "Pet_sit", pose: "sit", loop: false, speed: 0, weight: 1 },
  { clip: "Drop_L_sit", pose: "sit", loop: false, speed: 0, weight: 1 },
  { clip: "Drop_R_sit", pose: "sit", loop: false, speed: 0, weight: 1 },

  // ─── lie ───────────────────────────────────────────────────────────
  {
    clip: "Lie_idle",
    pose: "lie",
    loop: true,
    speed: 0,
    durMin: 10,
    durMax: 22,
    weight: 4,
  },
  { clip: "Pet_lie", pose: "lie", loop: false, speed: 0, weight: 2 },

  // ─── sleep ─────────────────────────────────────────────────────────
  // Long dwell — cat sleeps for a while. No one-shots.
  {
    clip: "Sleep_idle",
    pose: "sleep",
    loop: true,
    speed: 0,
    durMin: 25,
    durMax: 60,
    weight: 4,
  },
];

// Turn clips inserted by the AI when heading changes during stand-pose
// locomotion. Never picked from the random pool.
export const TURN_CLIPS = ["Turn-45_L", "Turn-45_R", "Turn-90_L", "Turn-90_R"];
